import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Cpu,
  Layers,
  Flame,
  FileText,
  Calendar,
  Loader2,
  Clock,
  Tag,
  Info,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { machinesAPI, sopsAPI, workOrdersAPI } from "../api";

const initialForm = {
  workOrderNumber: "",
  machineId: "",
  title: "",
  description: "",
  workOrderType: "preventive",
  priority: "medium",
  sopId: "",
  scheduledStart: "",
  scheduledEnd: "",
  recommendedAction: "",
};

const WorkOrderCreate = ({
  isOpen = true,
  onClose,
  initialData = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    ...initialForm,
    ...(location.state || {}),
    ...(initialData || {}),
  });

  const fromMaintenanceLog = Boolean(form.maintenanceLogId);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/work-orders");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm((prev) => ({
        ...prev,
        ...(location.state || {}),
        ...(initialData || {}),
      }));
    }
  }, [isOpen, location.state, initialData]);

  const { data: machines = [], isLoading: machinesLoading } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((response) => response.data.data || []),
    enabled: isOpen,
  });

  const { data: sops = [], isLoading: sopsLoading } = useQuery({
    queryKey: ["work-order-sops", form.machineId],
    queryFn: () =>
      sopsAPI
        .getAll({ machineId: form.machineId, status: "approved", limit: 100 })
        .then((response) => response.data.data || []),
    enabled: isOpen && Boolean(form.machineId),
  });

  const createMutation = useMutation({
    mutationFn: (data) => workOrdersAPI.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      toast.success("Work order created successfully!");
      handleClose();
      if (!onClose) {
        navigate(`/work-orders/${response.data.data.id}`);
      }
    },
    onError: (error) =>
      toast.error(error.response?.data?.error || "Unable to create work order"),
  });

  const update = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    if (!form.machineId) {
      toast.error("Please select a target machine");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Work order title / purpose is required");
      return;
    }
    createMutation.mutate({
      ...form,
      sopId: form.sopId || null,
      scheduledStart: form.scheduledStart || null,
      scheduledEnd: form.scheduledEnd || null,
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  {fromMaintenanceLog ? "Convert Ticket to Work Order" : "Create Work Order"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Plan, allocate resources, and issue formal field maintenance orders
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={submit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* Row 1: WO Number & Target Machine */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!fromMaintenanceLog && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Work Order Number
                    </label>
                    <div className="relative">
                      <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={form.workOrderNumber}
                        onChange={update("workOrderNumber")}
                        placeholder="Generated automatically"
                        className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                      />
                    </div>
                  </div>
                )}

                <div className={fromMaintenanceLog ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Target Machine <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Cpu className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      required
                      value={form.machineId}
                      disabled={machinesLoading || fromMaintenanceLog}
                      onChange={update("machineId")}
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {machinesLoading ? "Loading machines..." : "Select machine..."}
                      </option>
                      {machines.map((machine) => (
                        <option key={machine.id || machine._id} value={machine.id || machine._id}>
                          {machine.name} {machine.model ? `(${machine.model})` : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Row 2: Title */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  {fromMaintenanceLog ? "Work Order Purpose" : "Title / Purpose"}{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <ClipboardList className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={update("title")}
                    placeholder="Describe the operational goal of this work order..."
                    className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                  />
                </div>
              </div>

              {/* Row 3: Type & Priority */}
              {!fromMaintenanceLog && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Order Type
                    </label>
                    <div className="relative">
                      <Layers className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <select
                        value={form.workOrderType}
                        onChange={update("workOrderType")}
                        className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer"
                      >
                        <option value="preventive">Preventive Maintenance</option>
                        <option value="corrective">Corrective Repair</option>
                        <option value="predictive">Predictive Servicing</option>
                        <option value="inspection">Safety Inspection</option>
                        <option value="emergency">Emergency Response</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Priority Level
                    </label>
                    <div className="relative">
                      <Flame className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <select
                        value={form.priority}
                        onChange={update("priority")}
                        className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                        <option value="critical">Critical (Immediate)</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              )}

              {/* Row 4: Linked SOP */}
              {!fromMaintenanceLog && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Standard Operating Procedure
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      value={form.sopId}
                      disabled={!form.machineId || sopsLoading}
                      onChange={update("sopId")}
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {!form.machineId
                          ? "Select machine first"
                          : sopsLoading
                          ? "Loading SOPs..."
                          : "No SOP attached (Optional)"}
                      </option>
                      {sops.map((sop) => (
                        <option key={sop.id || sop._id} value={sop.id || sop._id}>
                          {sop.title}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Row 5: Scheduled Start & End */}
              {!fromMaintenanceLog && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Scheduled Start Time
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="datetime-local"
                        value={form.scheduledStart}
                        onChange={update("scheduledStart")}
                        className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Scheduled Completion Time
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="datetime-local"
                        value={form.scheduledEnd}
                        onChange={update("scheduledEnd")}
                        className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Row 6: Description */}
              {!fromMaintenanceLog && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Planned Work Scope & Details
                  </label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={update("description")}
                    placeholder="Describe specific tasks, equipment areas, or observed conditions..."
                    className="w-full p-3 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs resize-none"
                  />
                </div>
              )}

              {/* Row 7: Recommended Action */}
              {!fromMaintenanceLog && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Recommended Action Steps
                  </label>
                  <textarea
                    rows={2}
                    value={form.recommendedAction}
                    onChange={update("recommendedAction")}
                    placeholder="Provide recommended repair methods, safety precautions, or tools..."
                    className="w-full p-3 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs resize-none"
                  />
                </div>
              )}

              {/* Informational Callout */}
              <div className="p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-start gap-3">
                <div className="p-1 rounded-lg bg-blue-100 text-blue-700 shrink-0 mt-0.5">
                  <Info className="w-4 h-4" />
                </div>
                <p className="text-xs text-blue-800 leading-relaxed font-medium">
                  {fromMaintenanceLog
                    ? "This will link this formal work order to the completed maintenance log for full traceability."
                    : "Once published, technicians can claim this order, log step progress, and document field evidence."}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-1.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    Creating...
                  </>
                ) : (
                  <>
                    <ClipboardList className="w-4 h-4 mr-1" />
                    {fromMaintenanceLog ? "Convert to Work Order" : "Create Work Order"}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default WorkOrderCreate;
